import React, { useState, useEffect, useCallback } from 'react';
import { randomizeLeftRight } from '../utils/randomization';
import { generatePlaceholderSVG } from '../utils/placeholderGenerator';

function Trial({ trial, trialNumber, totalTrials, artistName, onComplete, skipTimers = false }) {
  const [phase, setPhase] = useState(skipTimers ? 'choice' : 'fixation');
  const [startTime, setStartTime] = useState(null);
  const [swapImages, setSwapImages] = useState(() => randomizeLeftRight());
  const [selectedChoice, setSelectedChoice] = useState(null);
  const [confidence, setConfidence] = useState(null);
  const [responseEnabled, setResponseEnabled] = useState(skipTimers);
  
  // Swap images and metadata if randomized
  const leftImage = swapImages ? trial.rightImage : trial.leftImage;
  const rightImage = swapImages ? trial.leftImage : trial.rightImage;
  const leftType = swapImages ? trial.rightType : trial.leftType;
  const rightType = swapImages ? trial.leftType : trial.rightType;
  const leftPrompt = swapImages ? trial.rightPrompt : trial.leftPrompt;
  const rightPrompt = swapImages ? trial.leftPrompt : trial.rightPrompt;
  const leftTitle = swapImages ? trial.rightTitle : trial.leftTitle;
  const rightTitle = swapImages ? trial.leftTitle : trial.rightTitle;
  const leftModel = swapImages ? trial.rightModel : trial.leftModel;
  const rightModel = swapImages ? trial.leftModel : trial.rightModel;

  useEffect(() => {
    // Reset phase and randomization when trial changes
    setPhase(skipTimers ? 'choice' : 'fixation');
    setSwapImages(randomizeLeftRight());
    setSelectedChoice(null);
    setConfidence(null);
    setResponseEnabled(skipTimers);
    
    if (!skipTimers) {
      // Show fixation cross for 500ms
      const fixationTimer = setTimeout(() => {
        setPhase('choice');
        // Add 2-second delay before enabling response
        setTimeout(() => {
          setResponseEnabled(true);
          setStartTime(Date.now());
        }, 2000);
      }, 500);
      return () => clearTimeout(fixationTimer);
    } else {
      setStartTime(Date.now());
    }
  }, [trial, trialNumber, skipTimers]);

  const handleChoice = useCallback((choice) => {
    if (phase !== 'choice' || selectedChoice !== null || !responseEnabled) return;
    
    const endTime = Date.now();
    const rt = endTime - startTime;
    
    // Record which image was chosen (accounting for randomization)
    const chosenType = choice === 'left' ? leftType : rightType;
    const chosenImage = choice === 'left' ? leftImage : rightImage;
    
    // Store the choice data temporarily
    window.tempTrialData = {
      trialNumber,
      trialType: trial.type,
      leftImage,
      rightImage,
      leftType,
      rightType,
      choice,
      chosenType,
      chosenImage,
      choiceRT: rt,
      timestamp: endTime
    };
    
    setSelectedChoice(choice);
    
    // Auto-scroll to show confidence scale
    setTimeout(() => {
      const confidenceSection = document.querySelector('.confidence-section');
      if (confidenceSection) {
        confidenceSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  }, [phase, selectedChoice, startTime, trial, trialNumber, leftImage, rightImage, leftType, rightType, responseEnabled]);

  const handleConfidenceSubmit = () => {
    if (!selectedChoice || !confidence) return;
    
    // Add confidence to the data
    window.tempTrialData.confidence = confidence;
    
    setPhase('diagnostic');
  };

  const handleDiagnostic = (reason) => {
    const completeData = {
      ...window.tempTrialData,
      diagnosticReason: reason
    };
    delete window.tempTrialData;
    
    setPhase('iti');
    
    // Inter-trial interval with jitter (400-800ms)
    const itiDuration = 400 + Math.random() * 400;
    setTimeout(() => {
      onComplete(completeData);
    }, itiDuration);
  };

  // Keyboard event handler
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (phase === 'choice') {
        if (e.key === 'f' || e.key === 'F') {
          handleChoice('left');
        } else if (e.key === 'j' || e.key === 'J') {
          handleChoice('right');
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [phase, handleChoice]);

  // Generate descriptive placeholder images with prompts/titles and type labels
  const getImageSrc = (src, type, prompt, title, modelId) => {
    // Always generate placeholder for local paths
    if (!src || typeof src !== 'string' || !src.startsWith('http')) {
      let description = '';
      let typeLabel = '';
      
      // Determine the type label
      if (type === 'real') {
        typeLabel = 'REAL PAINTING';
      } else if (type === 'ai-typical') {
        typeLabel = 'AI-GENERATED: TYPICAL SUBJECT';
      } else if (type === 'ai-plausible') {
        typeLabel = 'AI-GENERATED: ATYPICAL BUT PLAUSIBLE';
      } else if (type === 'ai-anachronistic') {
        typeLabel = 'AI-GENERATED: ATYPICAL & ANACHRONISTIC';
      }
      
      if (type === 'real' && title) {
        // For real paintings, show the artwork title
        description = `"${title}" by ${artistName}`;
      } else if (prompt) {
        // For AI images, use the model ID if provided, otherwise try to determine from path
        let model = 'AI-Generated';
        if (modelId) {
          // Map model IDs to display names
          const modelNames = {
            'midjourney-v6': 'Midjourney v6',
            'dalle3': 'DALL-E 3',
            'flux-pro': 'Flux Pro',
            'sdxl-base': 'SDXL',
            'sdxl-finetuned': 'SDXL Fine-tuned'
          };
          model = modelNames[modelId] || modelId;
        } else if (src) {
          // Fallback to path detection if no model ID provided
          if (src.includes('midjourney')) model = 'Midjourney v6';
          else if (src.includes('dalle3')) model = 'DALL-E 3';
          else if (src.includes('flux-pro')) model = 'Flux Pro';
          else if (src.includes('sdxl-base')) model = 'SDXL';
          else if (src.includes('sdxl-finetuned')) model = 'SDXL Fine-tuned';
        }
        description = `${model}: "${prompt}"`;
      } else {
        // Fallback to generic descriptions
        if (type === 'real') {
          description = `Real ${artistName} Painting`;
        } else if (type === 'ai-typical') {
          description = `AI: Typical ${artistName} Subject`;
        } else if (type === 'ai-plausible') {
          description = `AI: Plausible ${artistName} Subject`;
        } else if (type === 'ai-anachronistic') {
          description = `AI: Anachronistic ${artistName} Style`;
        } else {
          description = `${artistName} Style Image`;
        }
      }
      
      return generatePlaceholderSVG(description, 400, 400, '#6677aa', typeLabel);
    }
    
    return src;
  };

  // Get the trial family description based on the trial data
  const getTrialTypeDescription = () => {
    // Define explanations for each trial type
    const explanations = {
      T1: 'This trial compares AI-generated images with real artworks matched on subject matter. By holding content constant, we isolate the pure stylistic comparison. This is the primary measure of whether AI models can capture authentic artistic style.',
      T2: 'This trial compares images from different AI models using the same prompt. This helps us rank which AI models better capture this artist\'s style, independent of comparisons to real artwork.',
      T3: 'This trial compares two genuine artworks by the same artist. Since both are authentic, this establishes the "human ceiling" - how consistently humans judge style when both options are real. This calibrates what level of performance is achievable.',
      T4: 'This trial tests whether the AI model relies on typical subject matter or can maintain stylistic consistency with unusual subjects. One image shows a typical subject for this artist, the other shows something they never painted (either anachronistic or just atypical).',
      practice: 'Practice trials help you understand the task before the main experiment begins. These don\'t count toward the results.',
      catch: 'Attention check trials contain obvious style mismatches to ensure participants are paying attention. Failing these may result in data exclusion.',
      repeated: 'This is an exact repeat of an earlier trial to measure response consistency. Participants who give very different answers to repeated trials may not be paying attention.'
    };
    
    // Check for repeated trials first (they might have a trialFamily too)
    if (trial.isRepeated) {
      // If it's a repeated trial, show both the original type and that it's repeated
      if (trial.trialFamily === 'T1') {
        return { 
          text: 'T1 (Repeated): Fidelity, content-matched (AI vs Real)', 
          isSpecial: true,
          tooltip: explanations.repeated + ' The original trial was: ' + explanations.T1
        };
      } else if (trial.trialFamily === 'T2') {
        return { 
          text: 'T2 (Repeated): Cross-model comparisons (AI vs AI)', 
          isSpecial: true,
          tooltip: explanations.repeated + ' The original trial was: ' + explanations.T2
        };
      } else if (trial.trialFamily === 'T3') {
        return { 
          text: 'T3 (Repeated): Human ceiling (Real vs Real)', 
          isSpecial: true,
          tooltip: explanations.repeated + ' The original trial was: ' + explanations.T3
        };
      } else if (trial.trialFamily === 'T4') {
        return { 
          text: 'T4 (Repeated): Content-style disentanglement (Within-model)', 
          isSpecial: true,
          tooltip: explanations.repeated + ' The original trial was: ' + explanations.T4
        };
      }
      return { 
        text: 'Repeated Trial: Consistency Check', 
        isSpecial: true,
        tooltip: explanations.repeated
      };
    }
    
    // Check for regular trial families
    if (trial.trialFamily === 'T1') {
      return { 
        text: 'T1: Fidelity, content-matched (AI vs Real)', 
        isSpecial: false,
        tooltip: explanations.T1
      };
    } else if (trial.trialFamily === 'T2') {
      return { 
        text: 'T2: Cross-model comparisons (AI vs AI)', 
        isSpecial: false,
        tooltip: explanations.T2
      };
    } else if (trial.trialFamily === 'T3') {
      return { 
        text: 'T3: Human ceiling (Real vs Real)', 
        isSpecial: false,
        tooltip: explanations.T3
      };
    } else if (trial.trialFamily === 'T4') {
      return { 
        text: 'T4: Content-style disentanglement (Within-model)', 
        isSpecial: false,
        tooltip: explanations.T4
      };
    } else if (trial.trialFamily === 'practice') {
      return { 
        text: 'Practice Trial', 
        isSpecial: true,
        tooltip: explanations.practice
      };
    } else if (trial.trialFamily === 'catch') {
      return { 
        text: 'Catch Trial: Attention Check', 
        isSpecial: true,
        tooltip: explanations.catch
      };
    }
    
    // Fallback for any trials without explicit family
    if (trial.isPractice) {
      return { 
        text: 'Practice Trial', 
        isSpecial: true,
        tooltip: explanations.practice
      };
    } else if (trial.isCatch) {
      return { 
        text: 'Catch Trial: Attention Check', 
        isSpecial: true,
        tooltip: explanations.catch
      };
    }
    
    // If no trial family is specified, return null (won't show label)
    return null;
  };

  const trialTypeDescription = getTrialTypeDescription();

  return (
    <div className="trial-container">
      {phase === 'fixation' && (
        <div className="fixation">+</div>
      )}
      
      {phase === 'choice' && (
        <>
          {trialTypeDescription && (
            <div 
              className={`trial-type-label ${trialTypeDescription.isSpecial ? 'special-trial' : ''}`}
              title={trialTypeDescription.tooltip}
            >
              {trialTypeDescription.isSpecial ? trialTypeDescription.text : `Trial type ${trialTypeDescription.text}`}
              <div className="trial-type-tooltip">
                {trialTypeDescription.tooltip}
              </div>
            </div>
          )}
          <div className="question">
            Which image is more characteristic of the style of {artistName}?
          </div>
          <div className="images-comparison">
            <div 
              className={`image-option ${selectedChoice === 'left' ? 'selected' : ''}`}
              onClick={() => handleChoice('left')}
            >
              <img 
                src={getImageSrc(leftImage, leftType, leftPrompt, leftTitle, leftModel)} 
                alt="Left option"
              />
            </div>
            <div 
              className={`image-option ${selectedChoice === 'right' ? 'selected' : ''}`}
              onClick={() => handleChoice('right')}
            >
              <img 
                src={getImageSrc(rightImage, rightType, rightPrompt, rightTitle, rightModel)} 
                alt="Right option"
              />
            </div>
          </div>
          
          {selectedChoice && (
            <div className="confidence-section">
              <div className="confidence-container">
                <div className="confidence-question">
                  How confident are you in your choice?
                </div>
                <div className="likert-scale">
                  <div className="likert-slider-container">
                    <div className="likert-labels">
                      <span>Very unsure</span>
                      <span>Unsure</span>
                      <span>Slightly unsure</span>
                      <span>Slightly sure</span>
                      <span>Sure</span>
                      <span>Very sure</span>
                    </div>
                    <div className="likert-track">
                      {[1, 2, 3, 4, 5, 6].map(value => (
                        <button
                          key={value}
                          className={`likert-point ${confidence === value ? 'selected' : ''}`}
                          onClick={() => setConfidence(value)}
                          aria-label={`Confidence level ${value}`}
                        >
                          <span className="likert-value">{value}</span>
                        </button>
                      ))}
                      <div className="likert-line"></div>
                    </div>
                  </div>
                </div>
                <button 
                  className="submit-button"
                  onClick={handleConfidenceSubmit}
                  disabled={!confidence}
                >
                  Submit
                </button>
              </div>
            </div>
          )}
          
          {!selectedChoice && (
            <p style={{ textAlign: 'center', color: '#666', fontSize: '14px' }}>
              {!responseEnabled ? (
                <span style={{ color: '#999' }}>Please examine the images carefully...</span>
              ) : (
                <>Press F for left image, J for right image | Trial {trialNumber} of {totalTrials}</>
              )}
            </p>
          )}
        </>
      )}
      
      {phase === 'diagnostic' && (
        <>
          <div className="question">
            What was the main factor that influenced your choice?
          </div>
          <div className="diagnostic-options">
            <div 
              className="diagnostic-option"
              onClick={() => handleDiagnostic('brushwork')}
            >
              Brushwork and texture
            </div>
            <div 
              className="diagnostic-option"
              onClick={() => handleDiagnostic('color')}
            >
              Color palette
            </div>
            <div 
              className="diagnostic-option"
              onClick={() => handleDiagnostic('composition')}
            >
              Composition
            </div>
            <div 
              className="diagnostic-option"
              onClick={() => handleDiagnostic('form')}
            >
              Form or shape language
            </div>
            <div 
              className="diagnostic-option"
              onClick={() => handleDiagnostic('other')}
            >
              Other
            </div>
            <div 
              className="diagnostic-option"
              onClick={() => handleDiagnostic('guessing')}
            >
              I don't know / I was guessing
            </div>
          </div>
        </>
      )}
      
      {phase === 'iti' && (
        <div style={{ height: '400px' }}></div>
      )}
    </div>
  );
}

export default Trial;